<div class="flex h-full w-full flex-col overflow-hidden rounded-ds-md border border-ds-border bg-ds-surface">
  @if (showHeader()) {
    <div class="flex flex-none flex-wrap items-center gap-2 border-b border-ds-border px-2 py-1.5">
      <h3 class="mr-auto text-sm font-semibold text-ds-text">{{ title() }}</h3>

      <!-- Same segmented control as the OCR history status tabs; `.seg-pill-active`
           is defined once in styles.scss so the two cannot drift. -->
      <div
        role="tablist"
        aria-label="Document representation"
        class="flex items-center gap-0.5 rounded-ds-md bg-ds-surface-muted p-0.5">
        <button
          type="button"
          role="tab"
          data-mode="pdf"
          data-testid="document-viewer-tab-pdf"
          [attr.aria-selected]="mode() === 'pdf'"
          [attr.tabindex]="mode() === 'pdf' ? 0 : -1"
          (click)="select('pdf')"
          (keydown)="onTabKeydown($event)"
          class="seg-pill inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-ds-sm px-2.5 text-xs font-medium text-ds-text-muted transition-colors duration-150 hover:text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
          [class.seg-pill-active]="mode() === 'pdf'">
          <mat-icon aria-hidden="true" class="!h-4 !w-4 !text-[16px] !leading-4">{{ originalIcon() }}</mat-icon>
          Original
        </button>

        <!-- Disabled rather than hidden: a reader who expects markdown should be
             told it has not been produced yet, not left to wonder where it went.
             The tooltip sits on the wrapper because a disabled button dispatches
             no pointer events of its own — which is also why the button drops
             them when disabled, so the hover reaches this span. -->
        <span
          class="inline-flex"
          [class.cursor-not-allowed]="!hasMarkdown()"
          [matTooltip]="
            hasMarkdown()
              ? 'Show the markdown the extraction agents read'
              : 'Markdown is not available for this document yet'
          ">
          <button
            type="button"
            role="tab"
            data-mode="md"
            data-testid="document-viewer-tab-md"
            [disabled]="!hasMarkdown()"
            [attr.aria-selected]="mode() === 'md'"
            [attr.aria-disabled]="!hasMarkdown()"
            [attr.tabindex]="mode() === 'md' ? 0 : -1"
            (click)="select('md')"
            (keydown)="onTabKeydown($event)"
            class="seg-pill inline-flex h-7 items-center gap-1.5 rounded-ds-sm px-2.5 text-xs font-medium text-ds-text-muted transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary enabled:cursor-pointer enabled:hover:text-ds-text disabled:pointer-events-none disabled:opacity-45"
            [class.seg-pill-active]="mode() === 'md'">
            <mat-icon aria-hidden="true" class="!h-4 !w-4 !text-[16px] !leading-4">description</mat-icon>
            Markdown
          </button>
        </span>
      </div>

      @if (externalUrl(); as href) {
        <a
          [href]="href"
          target="_blank"
          rel="noopener"
          data-testid="document-viewer-open-external"
          [matTooltip]="externalLabel()"
          [attr.aria-label]="externalLabel()"
          class="inline-flex h-7 w-7 items-center justify-center rounded-ds-sm text-ds-text-muted transition-colors duration-150 hover:bg-ds-surface-muted hover:text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary">
          <mat-icon aria-hidden="true" class="!h-4 !w-4 !text-[16px] !leading-4">open_in_new</mat-icon>
        </a>
      }
    </div>
  }

  <div class="min-h-0 flex-1">
    @if (mode() === 'pdf') {
      <!-- @if rather than [hidden]: leaving both panes alive keeps the browser's
           PDF plugin resident, and this component lives inside an already
           memory-hungry resizable split. -->
      @if (showsDownload() && pdfUrl()) {
        <div
          class="viewer-pane flex h-full w-full flex-col items-center justify-center gap-3 bg-ds-bg p-6 text-center"
          data-testid="document-viewer-download-panel">
          <mat-icon aria-hidden="true" class="!h-12 !w-12 !text-[48px] !leading-[48px] text-ds-text-muted">insert_drive_file</mat-icon>
          <p class="max-w-sm text-sm text-ds-text-muted">
            This file type cannot be previewed in the browser. The Markdown tab shows what the extraction agents read.
          </p>
          <a
            [href]="pdfUrl()"
            download
            rel="noopener"
            data-testid="document-viewer-download"
            class="inline-flex h-9 items-center gap-1.5 rounded-ds-sm border border-ds-border bg-ds-surface px-3 text-sm font-medium text-ds-text transition-colors duration-150 hover:bg-ds-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary">
            <mat-icon aria-hidden="true" class="!h-4 !w-4 !text-[16px] !leading-4">download</mat-icon>
            Download original
          </a>
        </div>
      } @else if (isImage() && pdfUrl()) {
        <div class="viewer-pane h-full w-full overflow-auto bg-ds-bg p-2">
          <img
            [src]="pdfUrl()"
            alt="Original document image"
            class="mx-auto block h-auto max-w-full"
            data-testid="document-viewer-image"
            (error)="imageLoadFailed.set(true)" />
        </div>
      } @else if (safePdfUrl(); as src) {
        <iframe
          [src]="src"
          class="viewer-pane h-full w-full border-0"
          data-testid="document-viewer-pdf"
          [title]="title()"></iframe>
      } @else {
        <div
          class="flex h-full items-center justify-center bg-ds-bg p-6 text-sm text-ds-text-muted"
          data-testid="document-viewer-pdf-empty">
          Preview of the original is unavailable for this document.
        </div>
      }
    } @else {
      <app-markdown-view class="viewer-pane block h-full" [url]="markdownUrl()" />
    }
  </div>
</div>
